import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { CTASection } from '../components/CTASection';
import { ExternalLink } from 'lucide-react';

const projects = [
  {
    title: 'Microstar',
    category: 'Comedie dramatique',
    year: '2026',
    image: 'https://customer-assets.emergentagent.com/job_cinema-mission/artifacts/0ai2x95g_WhatsApp%20Image%202026-07-08%20at%2010.26.09.jpeg',
    link: 'https://www.allocine.fr/film/fichefilm_gen_cfilm=1000030390.html',
  },
  {
    title: 'Un Grand Raccourci',
    category: 'Comedie dramatique',
    year: '2026',
    image: 'https://customer-assets-rejwkqb3.emergentagent.net/job_cinema-mission/artifacts/o6w4r8ta_image.png',
    link: 'https://www.allocine.fr/film/fichefilm_gen_cfilm=1000041528.html',
  },
  {
    title: 'Mauvais Payeurs',
    category: 'Comedie',
    year: '2025',
    image: 'https://customer-assets-rejwkqb3.emergentagent.net/job_cinema-mission/artifacts/y7ryep0h_image.png',
    link: 'https://letterboxd.com/film/mauvais-payeurs/',
  },
  {
    title: 'Silent Friend',
    category: 'Drame',
    year: '2026',
    image: 'https://fr.web.img6.acsta.net/c_310_420/img/42/05/4205010710cd6378d3ec01f929478794.jpg',
    link: 'https://www.allocine.fr/film/fichefilm_gen_cfilm=315015.html',
  },
  {
    title: 'Qui brille au combat',
    category: 'Drame',
    year: '2026',
    image: 'https://customer-assets.emergentagent.com/job_cinema-mission/artifacts/5ztps1vn_QuiBrilleAuCombat.jpg',
    link: 'https://www.allocine.fr/film/fichefilm_gen_cfilm=1000012015.html',
  },
  {
    title: 'Itzamna',
    category: 'Thriller, drame',
    year: '2024',
    image: 'https://cdn.prod.website-files.com/65bab6e8de90f51ce337cb55/680799d3984916e3b2ef124d_1734707556595.jpeg',
    link: 'https://secondsouffleproduction.fr/portfolio/itzamna',
  },
  {
    title: 'Nube',
    category: 'Aventure, animation',
    year: '2024',
    image: 'https://cdn.prod.website-files.com/65bab6e8de90f51ce337cb55/6745f783d8e9c578cbf36d1a_Capture%20d%E2%80%99e%CC%81cran%202024-11-26%20a%CC%80%2017.29.45.png',
    link: 'https://secondsouffleproduction.fr/portfolio/nube',
  },
  {
    title: 'Dino Burgers',
    category: 'Comedie dramatique',
    year: '2026',
    image: '/dino-burgers-poster.png',
    link: 'https://secondsouffleproduction.fr/portfolio/dino-burgers',
  },
  {
    title: 'Gretel',
    category: 'Animation adulte',
    year: '2026',
    image: '/gretel-poster.png',
    link: 'https://secondsouffleproduction.fr/portfolio/gretel',
  },
  {
    title: 'Le Vieux Prince',
    category: 'Adaptation',
    year: '2026',
    image: '/le-vieux-prince-poster.png',
    link: 'https://secondsouffleproduction.fr/portfolio/le-vieux-prince',
  },
  {
    title: 'Affamee et fachee',
    category: 'Comedie noire',
    year: '2025',
    image: 'https://cdn.prod.website-files.com/65bab6e8de90f51ce337cb55/683f86af0a566348682b4714_Miniature.png',
    link: 'https://secondsouffleproduction.fr/portfolio/affamee-et-fachee',
  },
];

const stats = [
  { value: '20+', label: 'Projets' },
  { value: '400k+', label: 'Fonds levés' },
];

const ProjectsPage = () => {
  const heroRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true });
  
  const gridRef = useRef(null);
  const isGridInView = useInView(gridRef, { once: true, margin: '-50px' });

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="projects-page">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-16 md:pt-40 md:pb-20 bg-[#050505]" data-testid="projects-hero">
          <div className="container-custom">
            <motion.div
              ref={heroRef}
              initial={{ opacity: 0, y: 30 }}
              animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="max-w-4xl"
            >
              <span className="overline mb-6 block">Portfolio</span>
              <h1 className="text-4xl md:text-6xl font-medium mb-6 leading-tight">
                Nos <span className="text-gradient">projets</span>
              </h1>
              <p className="text-[#a1a1aa] text-lg md:text-xl leading-relaxed max-w-3xl mb-10">
                Découvrez nos projets en développement, production et post-production : 
                des œuvres cinématographiques uniques qui reflètent notre engagement pour 
                la singularité, la créativité et l'impact social. Nous menons également 
                des missions d'intérêt général, comme la distribution non-commerciale.
              </p>

              {/* Stats */}
              <div className="flex flex-wrap gap-6 mb-10">
                {stats.map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                    className="glass px-8 py-6 rounded-2xl text-center min-w-[140px]"
                  >
                    <div className="stat-number text-3xl md:text-4xl mb-1">{stat.value}</div>
                    <div className="text-[#a1a1aa] text-sm">{stat.label}</div>
                  </motion.div>
                ))}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.5 }}
                  className="inline-block"
                >
                  <Link
                    to="/faire-un-don"
                    className="btn-primary flex items-center justify-center"
                    data-testid="projects-donate-btn"
                  >
                    Faire un don
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Projects Grid */}
        <section className="py-16 md:py-24 bg-[#0a0a0c]" data-testid="projects-grid">
          <div className="container-custom">
            <div 
              ref={gridRef}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            >
              {projects.map((project, index) => (
                <motion.div
                  key={project.title}
                  initial={{ opacity: 0, y: 40 }}
                  animate={isGridInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group relative rounded-2xl overflow-hidden bg-[#121215] card-hover cursor-default"
                  data-testid={`project-card-${index}`}
                >
                  {/* Image */}
                  <div className="aspect-[3/4] overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-300" />
                  </div>

                  {/* Content */}
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[#D4AF37] text-xs font-medium">{project.category}</span>
                      <span className="text-[#71717a] text-xs">/ {project.year}</span>
                    </div>
                    <h3 className="text-lg font-medium text-white transition-colors duration-300">
                      {project.title}
                    </h3>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <CTASection />
      </main>

      <Footer />
    </div>
  );
};

export default ProjectsPage;
